import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import numeral from 'numeral';
import { Link } from 'react-router-dom';
import { Query } from '@apollo/react-components';
import { Button } from 'antd';
import { reverse } from 'named-urls';
import routes from '@achievable-me/tundra-wizard/routes';
import { hasFullAccess } from '@achievable-me/tundra-wizard/auth';
import Well from '$components/ProductDashboard/Well';
import ProductLayout from '$components/layout/ProductLayout';
import SpinnerError from '$components/library/SpinnerError';
import Seo from '$components/Seo';
import greScales from '$components/review/ReviewMain/GenericSummary/greScales';
import PRODUCT_USER_EXAMS from '$queries/productUserExams';
import { useTrack } from '$lib/tracking';
import TakeExams from './TakeExams';
import GreInstructions from './GreInstructions';
import HeaderContent from './HeaderContent';
import MyExamTile from './MyExamTile';
import styles from './styles.scss';
import textStyles from './textStyles.scss';
import ExamFeedback from './ExamFeedback';

const VARIANT_TITLE_MAP = {
  quant: 'Quantitative reasoning',
  verbal: 'Verbal reasoning',
  issue: 'Analytical writing (issue)',
  argument: 'Analytical writing (argument)',
};

const getTitleFromVariant = (variant) => {
  const title = VARIANT_TITLE_MAP[variant];
  return title || variant;
};

const UpgradeCta = ({ hasFull, productSlug }) => {
  if (hasFull) {
    return null;
  }

  return (
    <div className={styles.banner}>
      <div data-aaa="bannerSignUp" className={styles.bannerSignUpCta}>
        <div className={styles.bannerSignUpCtaText}>
          Buy the course to get full access to our exams and more!
        </div>
        <div>
          <Link to={reverse(routes.study.purchase, { productSlug })}>
            <Button data-aaa="upgradeButton">View info</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

const ExamsContent = (props) => {
  const { currentUser, product } = props;
  const { slug: productSlug } = product;
  useTrack('Exams page | Viewed', { productSlug });

  return (
    <>
      <Seo
        title="Practice exams"
        canonical={reverse(routes.study.exams, { productSlug: product.slug })}
        description={`Take full mock practice exams to prepare for real questions on the ${product.name}.`}
      />
      <Query
        query={PRODUCT_USER_EXAMS}
        variables={{ input: { slug: product.slug } }}
        fetchPolicy="network-only"
      >
        {({ loading, error, data }) => {
          if (loading || error) {
            return <SpinnerError error={error} />;
          }

          const hasFull = hasFullAccess({ user: currentUser, product });
          const takeExamsDisabled = !hasFull;

          const {
            product: {
              product: { userExams, essayResponses, userExamsFeedback },
            },
          } = data;

          const allThings = _.sortBy([...essayResponses, ...userExams], (thing) => {
            return thing.startedAt;
          }).reverse();

          const allThingsLength = allThings.length;
          const userExamsFeedbackLength = userExamsFeedback.length;

          return (
            <Well>
              <div className={styles.layout}>
                <h1>Practice exams</h1>
                <HeaderContent productSlug={productSlug} />
                <UpgradeCta hasFull={hasFull} productSlug={product.slug} />
                <TakeExams
                  currentUser={currentUser}
                  product={product}
                  disabled={takeExamsDisabled}
                  userExams={userExams}
                />
                {productSlug === 'gre' && <GreInstructions />}
                {userExamsFeedbackLength > 0 && (
                  <>
                    <h2 className={textStyles.header}>Your personalized exam guidance (beta)</h2>
                    <div>
                      Is this helpful? What else would you like to see? Please send us your <Link to={reverse(routes.study.feedback, { productSlug })}>feedback</Link>!
                      </div>
                    <div className={styles.yourExamFeedback}>
                      <ExamFeedback
                        product={product}
                        userExamsFeedback={userExamsFeedback}
                      />
                    </div>
                  </>
                )}
                {allThingsLength > 0 && (
                  <>
                    <h2 className={textStyles.header}>Your exams</h2>
                    <div className={styles.yourExams}>
                      {allThings.map((finishedThing, i) => {
                        const {
                          uuid,
                          score,
                          startedAt,
                          finishedAt,
                          variant,
                          submitAt,
                          points,
                          pointsTotal,
                          createdAt,
                          __typename,
                        } = finishedThing;

                        const isFinished = finishedAt || submitAt;
                        const text = isFinished ? 'Review' : 'Resume';
                        const buttonType = isFinished ? 'default' : 'primary';
                        const when = (() => {
                          const startedAtMoment = moment(startedAt || createdAt);
                          const startedAtF = startedAtMoment.format('MMMM Do, h:mm a');

                          if (!isFinished) {
                            return startedAtF;
                          }

                          const endedAtMoment = moment(finishedAt || submitAt);

                          const minutesUsed = Math.floor(
                            moment.duration(endedAtMoment - startedAtMoment).asMinutes()
                          );

                          return `${startedAtF} (${minutesUsed} mins)`;
                        })();

                        if (__typename === 'EssayResponse') {
                          const {
                            essayPrompt: { type },
                          } = finishedThing;

                          const title = getTitleFromVariant(type);
                          const scoreF = isFinished ? numeral(score).format('0.0') : '--';
                          const dataAaa = isFinished ? 'reviewEssay' : 'resumeEssay';

                          return (
                            <MyExamTile
                              key={uuid}
                              title={title}
                              score={scoreF}
                              when={when}
                              cta={{
                                'data-aaa': dataAaa,
                                'data-product-slug': productSlug,
                                text,
                                type: buttonType,
                                to: reverse(routes.study.essay, {
                                  productSlug,
                                  essayResponseUuid: uuid,
                                }),
                              }}
                            />
                          );
                        }

                        const title = (() => {
                          if (productSlug === 'gre') {
                            return getTitleFromVariant(variant);
                          }

                          return `Practice exam #${allThingsLength - i}`;
                        })();

                        const scoreF = (() => {
                          if (!isFinished) {
                            return '--%';
                          }

                          if (productSlug === 'gre') {
                            const greScaled = greScales({ variant, points });
                            const { range } = greScaled;
                            return range;
                          }

                          return numeral(score).format('0%');
                        })();

                        const scoreInfo = (() => {
                          if (!isFinished) {
                            return null;
                          }

                          if (productSlug === 'gre') {
                            return `Raw score: ${numeral(score).format(
                              '0%'
                            )} (${points}/${pointsTotal})`;
                          }

                          return `Raw score: ${points}/${pointsTotal}`;
                        })();

                        const dataAaa = isFinished ? 'reviewExam' : 'resumeExam';

                        return (
                          <MyExamTile
                            key={uuid}
                            title={title}
                            score={scoreF}
                            scoreInfo={scoreInfo}
                            when={when}
                            cta={{
                              'data-aaa': dataAaa,
                              'data-product-slug': productSlug,
                              text,
                              type: buttonType,
                              to: reverse(routes.study.exam, {
                                productSlug,
                                userExamUuid: uuid,
                              }),
                            }}
                          />
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </Well>
          );
        }}
      </Query>
    </>
  );
};

const Exams = (props) => {
  const { currentUser, location, match } = props;

  return (
    <ProductLayout currentUser={currentUser} location={location} match={match}>
      {({ product }) => <ExamsContent currentUser={currentUser} product={product} />}
    </ProductLayout>
  );
};

export default Exams;
